/**
 * The AI Business Summary.
 *
 * Reads the period back to the owner in plain language. Every sentence comes
 * from the API alongside the figures it describes, so the prose and the tiles
 * are the same numbers rendered two ways — and any sentence that names a
 * dimension is clickable straight through to the transactions behind it.
 */

import type { ReactNode } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Minus,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import type { BusinessNarrative, Insight } from '@cenox/shared';
import { Card, cn } from '../../components/ui';
import type { DrilldownTarget } from './DrilldownModal';

const TONE_STYLES: Record<Insight['tone'], { dot: string; icon: typeof ArrowUpRight }> = {
  positive: { dot: 'text-emerald-600', icon: ArrowUpRight },
  negative: { dot: 'text-red-600', icon: ArrowDownRight },
  neutral: { dot: 'text-slate-400', icon: Minus },
};

export function NarrativeCard({
  narrative,
  onDrill,
}: {
  narrative: BusinessNarrative;
  onDrill: (target: DrilldownTarget) => void;
}): ReactNode {
  return (
    <Card className="border-brand-200 bg-gradient-to-br from-brand-50/80 to-white">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
          <Sparkles className="size-4.5" aria-hidden />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="text-base font-semibold text-slate-900">Business summary</h2>
            <span className="text-xs text-slate-500">
              generated from this period&rsquo;s figures
            </span>
          </div>

          <p className="mt-2 text-[15px] font-medium leading-relaxed text-slate-900">
            {narrative.headline}
          </p>

          {narrative.insights.length > 0 && (
            <ul className="mt-3 space-y-2">
              {narrative.insights.map((insight, index) => {
                const tone = TONE_STYLES[insight.tone];
                const Icon = tone.icon;
                const drillable = insight.dimension !== null && insight.dimensionKey !== null;

                const body = (
                  <span className="flex items-start gap-2 text-left">
                    <Icon
                      className={cn('mt-0.5 size-4 shrink-0', tone.dot)}
                      aria-hidden
                    />
                    <span className="text-sm leading-relaxed text-slate-700">{insight.text}</span>
                  </span>
                );

                return (
                  <li key={index}>
                    {drillable ? (
                      <button
                        type="button"
                        onClick={() =>
                          onDrill({
                            label: insight.text.split(' ').slice(0, 4).join(' '),
                            kind: insight.kind === 'expenses' ? 'expenses' : 'sales',
                            dimension: insight.dimension ?? undefined,
                            key: insight.dimensionKey ?? undefined,
                          })
                        }
                        className="w-full rounded-lg px-2 py-1 -mx-2 text-left transition-colors hover:bg-white/70"
                        title="Show the transactions behind this"
                      >
                        {body}
                      </button>
                    ) : (
                      <span className="block px-2 py-1 -mx-2">{body}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!narrative.hasActivity && (
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <TrendingUp className="size-4" aria-hidden />
              Figures will appear here once there is trading activity in this period.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
